import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="11315" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative font-sans text-sm font-medium text-muted-foreground transition-colors hover:text-primary py-2 block group tracking-tight">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <div className="flex flex-col gap-4 p-6 bg-card border-t border-border/40">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-sans text-lg font-medium text-muted-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4">
        <Button className="w-full rounded-md font-sans bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20" size="lg">
          Get Started
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`dark fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/80 backdrop-blur-xl shadow-xl shadow-black/20 border-border/40" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0">
          <img src="https://quotient.technology/wp-content/uploads/2026/03/image_8jv31ngh.png" alt="Quotient Technology" className="h-7 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:block">
          <Button variant="outline" className="rounded-md font-sans px-6 border-primary/20 hover:bg-primary/10 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)] transition-all text-foreground" size="default">
            Get Started
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background shadow-2xl animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}